"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import {
  TURNSTILE_SCRIPT_SRC,
  TURNSTILE_SITE_KEY,
  turnstileEnabled,
  turnstileLanguage,
} from "@/lib/turnstile";

// The Cloudflare Turnstile widget for the auth forms (see lib/turnstile.ts).
//
// `appearance: "interaction-only"`: the widget is invisible unless Cloudflare
// wants a click, so an ordinary visitor sees nothing at all. Tokens are SINGLE
// USE and last 300 s, so a form calls `reset()` after every auth call it makes,
// successful or not, and `refresh-expired: auto` renews an idle one.
//
// Without a site key the hook renders nothing, reports "off", and `guard()`
// always passes: the forms then behave exactly as they did before Turnstile.

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<TurnstileApi> | null = null;

/** One script tag for the whole page, however many widgets mount. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = TURNSTILE_SCRIPT_SRC;
      s.async = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("no turnstile")));
      s.onerror = () => {
        loader = null; // a later mount may try again
        s.remove();
        reject(new Error("turnstile script failed"));
      };
      document.head.appendChild(s);
    });
  }
  return loader;
}

export type TurnstileStatus = "off" | "loading" | "ready" | "error";

export function useTurnstile(action: string) {
  const enabled = turnstileEnabled(TURNSTILE_SITE_KEY);
  const locale = useLocale();
  // A callback ref held in STATE, not a ref object: the form that holds the
  // container can unmount while this hook lives on (LoginForm swaps its
  // password form for the link form and back), and the widget must be
  // rendered again into the NEW container. With a ref object the effect never
  // re-ran, so after a round trip there was no widget, no token, and every
  // attempt stopped at "One moment".
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(enabled ? "loading" : "off");

  useEffect(() => {
    if (!enabled || !container) return;
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        const el = container;
        if (cancelled) return;
        widgetId.current = api.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          language: turnstileLanguage(locale),
          appearance: "interaction-only",
          theme: "light",
          size: "flexible",
          "refresh-expired": "auto",
          // We read the token from the callback; no hidden input of its own.
          "response-field": false,
          callback: (t: string) => {
            setToken(t);
            setStatus("ready");
          },
          "expired-callback": () => setToken(null),
          "timeout-callback": () => setToken(null),
          "error-callback": () => {
            setToken(null);
            setStatus("error");
            // Returning true stops Turnstile logging the error a second time;
            // it keeps retrying on its own.
            return true;
          },
        });
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
      const id = widgetId.current;
      widgetId.current = null;
      if (id) window.turnstile?.remove(id);
      // A token from a removed widget is not one to send.
      setToken(null);
    };
  }, [enabled, container, action, locale]);

  /** Spend the token: call after every auth request that carried it. */
  const reset = useCallback(() => {
    setToken(null);
    const id = widgetId.current;
    if (id) window.turnstile?.reset(id);
  }, []);

  /**
   * Null when the form may submit; otherwise the `authErrors` key to show.
   * Never blocks when Turnstile is off.
   */
  const guard = useCallback((): "captchaPending" | "captchaUnavailable" | null => {
    if (!enabled || token) return null;
    return status === "error" ? "captchaUnavailable" : "captchaPending";
  }, [enabled, token, status]);

  // `empty:hidden` keeps the container from adding a form gap while Cloudflare
  // has nothing to show.
  const widget = enabled ? <div ref={setContainer} className="empty:hidden" /> : null;

  return { enabled, token, status, reset, guard, widget };
}
